import * as React from 'react';
import type { SortingRule } from 'react-table';
import type { Scope, SpraypaintBase } from 'spraypaint';
import type { ICustomSearchEvent } from '../Search';
import { useSpraypaintNamedParams } from './useSpraypaint';

const transformSortForGraphiti = (sorts: SortingRule[]) => {
  const sortsForSpraypaint = sorts.map(({ id, desc }) => ({ [id]: desc ? 'desc' : 'asc' }));
  return Object.assign({}, ...sortsForSpraypaint);
};

interface IUseSpraypaintTableParams<TBase extends SpraypaintBase, TQuery = Record<string, any>, TMeta = any> {
  initialScope: Scope<TBase>;
  searchIdentifier: string;
  initialQuery?: TQuery;
  initialPageSize?: number;
  initialMeta?: TMeta;
  initialSort?: SortingRule[];
  initialExtraParams?: Record<string, unknown>;
  applyStatsToScope?: boolean;
}

export const useSpraypaintTableNamedParams = <
  TBase extends SpraypaintBase,
  TQuery = Record<string, any>,
  TMeta = unknown,
>({
  initialScope,
  searchIdentifier,
  initialQuery = null,
  initialPageSize = null,
  initialMeta = null,
  initialSort = [],
  initialExtraParams = null,
  applyStatsToScope = true,
}: IUseSpraypaintTableParams<TBase, TQuery, TMeta>) =>
  useSpraypaintTable<TBase, TQuery, TMeta>(
    initialScope,
    searchIdentifier,
    initialQuery,
    initialPageSize,
    initialMeta,
    initialSort,
    initialExtraParams,
    applyStatsToScope,
  );

/**
 * @deprecated (TimKrins) use useSpraypaintTableNamedParams instead please
 */
function useSpraypaintTable<TBase extends SpraypaintBase, TQuery = Record<string, any>, TMeta = unknown>(
  initialScope: Scope<TBase>,
  searchIdentifier: string,
  initialQuery: TQuery = null,
  initialPageSize: number = null,
  initialMeta: TMeta = null,
  initialSort: SortingRule[] = [],
  initialExtraParams: Record<string, unknown> = null,
  applyStatsToScope = true,
) {
  const {
    data,
    error,
    extraParams,
    fetchData,
    isInitialLoading,
    isLoading,
    meta,
    scope,
    order,
    page,
    pageSize,
    query,
    setExtraParams,
    setOrder,
    setPage,
    setPageSize,
    setQuery,
    setScope,
  } = useSpraypaintNamedParams<TBase, TQuery, TMeta>({
    initialScope: applyStatsToScope ? initialScope.stats({ total: 'count' }) : initialScope,
    initialQuery: initialQuery || ({} as TQuery),
    initialPageSize: initialPageSize || 10,
    initialMeta,
    initialExtraParams,
    initialOrder: transformSortForGraphiti(initialSort),
  });

  const total: number = (meta as any)?.stats?.total?.count || 0;
  const pages = Math.ceil(total / pageSize) || 1;

  function setSearch(searchTerm: string, query: TQuery = {} as TQuery) {
    handlePageChange(0);
    setQuery({ ...query, search: searchTerm } as TQuery);
  }

  const { search } = query as { search?: string };

  React.useEffect(() => {
    const eventListenerName = `${searchIdentifier}:search`;
    document.addEventListener(eventListenerName, handleSearch);

    return () => {
      document.removeEventListener(eventListenerName, handleSearch);
    };
  }, [searchIdentifier]);

  React.useEffect(() => {
    const eventListenerName = `${searchIdentifier}:fetchData`;
    document.addEventListener(eventListenerName, fetchData);

    return () => {
      document.removeEventListener(eventListenerName, fetchData);
    };
  }, [searchIdentifier, fetchData]);

  function handlePageSizeChange(newPageSize: number) {
    if (newPageSize) setPageSize(newPageSize);
  }

  function handlePageChange(newZeroIndexedPage: number) {
    // PageNumber is 0 indexed in ReactTable but starts for 1 for Graphiti
    setPage(newZeroIndexedPage + 1);
  }

  function handleSearch(event: ICustomSearchEvent) {
    setSearch(event.detail.searchTerm);
  }

  const zeroIndexedPage = page - 1;

  function handleSort(sorts: SortingRule[]) {
    setOrder(transformSortForGraphiti(sorts));
  }

  const tableProps = {
    data,
    /**
     * Takes a page, with zero representing the first page.
     * @param {number} page - The zero-indexed page.
     */
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    isInitialLoading,
    isLoading,
    /**
     * `manual` means that we will handling the sorting and pagination server-side
     */
    manual: true,
    meta: { pages, total },
    /**
     * The current page, with the first page being zero.
     */
    page: zeroIndexedPage || 0,
    pageSize,
    pages,
    setExtraParams,
    setOrder,
    query,
  };

  return {
    data,
    error,
    extraParams,
    fetchData,
    isInitialLoading,
    isLoading,
    meta,
    order,
    page,
    pageSize,
    query,
    scope,
    search,
    setExtraParams,
    setOrder,
    setPage,
    setPageSize,
    setQuery,
    setScope,
    setSearch,
    tableProps,
  };
}

export function dispatchFetchData(searchIdentifier: string) {
  document.dispatchEvent(new CustomEvent(`${searchIdentifier}:fetchData`));
}
